import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Languages, Palette } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { CONTACT } from "@/lib/contacts";

const NavLink = ({ href, children, onClick, idSuffix = "" }) => {
  const testId = `nav-link-${href.replace(/[#/]/g, "")}${idSuffix}`;
  const className =
    "block text-sm font-medium text-zinc-300 hover:text-[var(--brand)] transition-colors";
  return (
    <li>
      {href.startsWith("/blog") ? (
        <Link to={href} onClick={onClick} data-testid={testId} className={className}>
          {children}
        </Link>
      ) : (
        <a href={href} onClick={onClick} data-testid={testId} className={className}>
          {children}
        </a>
      )}
    </li>
  );
};

const Header = () => {
  const { t, lang, toggle } = useLanguage();
  const { theme, cycle } = useTheme();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const base = pathname === "/" ? "" : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  const links = (onClick, idSuffix = "") => (
    <>
      <NavLink href={`${base}#features`} onClick={onClick} idSuffix={idSuffix}>{t.nav.features}</NavLink>
      <NavLink href={`${base}#exchanges`} onClick={onClick} idSuffix={idSuffix}>{t.nav.exchanges}</NavLink>
      <NavLink href={`${base}#pricing`} onClick={onClick} idSuffix={idSuffix}>{t.nav.pricing}</NavLink>
      <NavLink href={`${base}#testimonials`} onClick={onClick} idSuffix={idSuffix}>{t.nav.testimonials}</NavLink>
      <NavLink href={`${base}#tutorial`} onClick={onClick} idSuffix={idSuffix}>{t.nav.tutorial}</NavLink>
      <NavLink href={`${base}#faq`} onClick={onClick} idSuffix={idSuffix}>{t.nav.faq}</NavLink>
      <NavLink href="/blog" onClick={onClick} idSuffix={idSuffix}>{t.nav.blog}</NavLink>
    </>
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#050505]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <a
          href={pathname === "/" ? "#top" : "/"}
          data-testid="brand-logo"
          aria-label="AIOTrade — beranda"
          className="flex items-center gap-2 font-heading text-lg font-extrabold tracking-tight"
        >
          <img
            src="/assets/aiotrade-logo.png?v=2"
            alt="AIOTrade"
            width="160"
            height="48"
            fetchPriority="high"
            decoding="async"
            className="h-10 md:h-12 w-auto object-contain"
          />
        </a>

        <nav aria-label={t.a11y.main_nav} className="hidden lg:block">
          <ul className="flex items-center gap-8">{links()}</ul>
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={cycle}
            data-testid="theme-toggle"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/15 text-white hover:border-[var(--brand)] hover:text-[var(--brand)] transition-colors"
            aria-label={`${t.a11y.theme} (${theme})`}
            title={`${t.a11y.theme} (${theme})`}
          >
            <Palette className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={toggle}
            data-testid="lang-toggle"
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-white/15 text-xs font-semibold uppercase tracking-wider text-white hover:border-[rgb(var(--brand-rgb)/0.6)] hover:text-[var(--brand)] transition-colors"
            aria-label={`${t.a11y.language} ${lang === "id" ? "English" : "Bahasa Indonesia"}`}
          >
            <Languages className="h-3.5 w-3.5" aria-hidden="true" />
            <span aria-hidden="true">{lang.toUpperCase()}</span>
          </button>
          <a
            href={CONTACT.registration}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-register-btn"
            className="hidden sm:inline-flex items-center px-5 h-10 rounded-full bg-[var(--brand)] text-black font-semibold text-sm hover:bg-[var(--brand-hover)] transition-colors"
          >
            {t.nav.register}
            <span className="sr-only"> {t.a11y.external}</span>
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white"
            data-testid="mobile-menu-toggle"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? t.a11y.close_menu : t.a11y.open_menu}
          >
            {open ? (
              <X className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Menu className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {open && (
        <nav
          id="mobile-menu"
          aria-label={t.a11y.mobile_nav}
          className="lg:hidden absolute top-16 left-0 right-0 bg-[#050505]/95 backdrop-blur-xl border-b border-white/10"
          data-testid="mobile-menu"
        >
          <ul className="flex flex-col px-6 py-6 gap-4">
            {links(close, "-mobile")}
            <li>
              <a
                href={CONTACT.registration}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-register-btn"
                className="mt-2 inline-flex w-full items-center justify-center px-5 h-11 rounded-full bg-[var(--brand)] text-black font-semibold text-sm"
              >
                {t.nav.register}
                <span className="sr-only"> {t.a11y.external}</span>
              </a>
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
};

export default Header;
